import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@core/authentication/auth.service';
import { isInspectorUser } from '@core/authentication/auth.utility';
import { ErrorDialogComponent } from '@shared/error-dialog/error-dialog.component';

@Component({
  selector: 'oiq-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  returnUrl: string;
  loginError: string;
  form: UntypedFormGroup;

  otpForm: UntypedFormGroup;

  mfaToken = '';
  private formSubmitAttempt: boolean;
  public loginInProgress = false;
  adminRoles = ['ONYXIQ_ADMIN', 'SUPER_ADMIN', 'ADMIN'];

  constructor(
    private fb: UntypedFormBuilder,
    private authService: AuthService,
    public dialog: MatDialog,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '';
    this.form = this.fb.group({
      userName: [
        '',
        {
          validators: Validators.compose([Validators.required, Validators.email]),
          updateOn: 'blur',
        },
      ],
      password: ['', Validators.required],
    });

    this.otpForm = this.fb.group({
      otp: ['', Validators.required],
      token: [this.mfaToken],
    });
  }

  isFieldInvalid(field: string) {
    return (
      (!this.form.get(field).valid && this.form.get(field).touched) ||
      (this.form.get(field).untouched && this.formSubmitAttempt)
    );
  }

  openNoWhiteLabelsDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '500px';
    dialogConfig.data =
      'You have no White Labels assigned, and will have a limited experience on the platform. Please contact your administrator.';
    const dialogRef = this.dialog.open(ErrorDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe();
  }

  onSubmit() {
    this.loginError = '';
    this.authService.killSession();

    if (!this.mfaToken) {
      if (this.form.valid) {
        this.authService.login(this.form.value, this.returnUrl).subscribe({
          next: result => {
            this.authService.createToken(result.apiKey);
            if (
              result.whiteLabelIds.length === 0 &&
              !this.adminRoles.includes(result.roles[0].name) &&
              !isInspectorUser(result.roles.map(role => role.name))
            ) {
              this.openNoWhiteLabelsDialog();
            }
          },
          error: error => {
            if (error.message) {
              if (error.message === 'Password expired') {
                this.loginError = 'Password has expired. You will be sent a password reset link';
              } else {
                this.loginError = error.message;
              }
            } else if (typeof error === 'string') {
              if (error === 'Password expired') {
                this.loginError = 'Password has expired. You will be sent a password reset link';
              } else {
                this.loginError = error;
              }
            } else {
              if (error.token) {
                this.mfaToken = error.token;
                this.otpForm.get('token').setValue(this.mfaToken);
              } else {
                this.loginError = 'An internal server error occurred.';
              }
            }
          },
          complete: () => {
            this.formSubmitAttempt = true;
          },
        });
      }
    } else {
      if (this.otpForm.valid) {
        this.authService.loginWithOtp(this.otpForm.value, this.returnUrl).subscribe({
          next: result => {
            this.authService.createToken(result.apiKey);
            if (
              result.whiteLabelIds.length === 0 &&
              !this.adminRoles.includes(result.roles[0].name) &&
              !isInspectorUser(result.roles.map(role => role.name))
            ) {
              this.openNoWhiteLabelsDialog();
            }
          },
          error: error => {
            if (typeof error === 'string') {
              this.loginError = error;
            } else {
              if (error.message) {
                this.loginError = error.message;
              } else {
                this.loginError = 'An internal server error occurred.';
              }
            }
          },
          complete: () => {
            this.formSubmitAttempt = true;
          },
        });
      }
    }
  }
}
